import { Box } from '@mantine/core';
import { RISK_DISPLAY_CONFIG, getRiskColor, getRiskIcon } from '../../constants/riskDisplay';
import { layout } from '../../theme/layout';
import { type IconSize } from '../../constants/iconSizes';
import type { RiskGrade } from '../../utils/riskGrade';
import { MaterialIcon } from './MaterialIcon';

/**
 * RiskIconBoxのサイズ設定
 */
export type RiskIconBoxSize = 'sm' | 'md' | 'lg';

const BOX_SIZES: Record<RiskIconBoxSize, { box: string; icon: IconSize }> = {
  sm: { box: '24px', icon: 'md' },
  md: { box: '32px', icon: 'xl' },
  lg: { box: '40px', icon: 'xxl' },
};

/**
 * RiskIconBoxのプロパティ
 *
 * `grade` と `riskLevel` のどちらか一方を指定する。両方指定された場合は `grade` が優先される。
 */
export interface RiskIconBoxProps {
  /** リスクタイプ（frost, wind, heavy_rain, crust, div） */
  riskType: string;
  /** HARC API の生リスク値（種別ごとにスケールが異なる）。`grade` 未指定時に使用 */
  riskLevel?: number;
  /** 意味的なリスク等級を直接指定（凡例など、生値でなく warning/caution を明示したい場合） */
  grade?: RiskGrade;
  /** サイズ（デフォルト: 'md'） */
  size?: RiskIconBoxSize;
  /** リスクがあるかどうか（falseの場合はアイコンを非表示） */
  hasRisk?: boolean;
  /** 追加のスタイル */
  style?: React.CSSProperties;
}

/**
 * リスクアイコンボックスコンポーネント
 *
 * DRY原則に基づき、カレンダー上のリスクアイコン表示を共通化。
 * - リスクタイプに応じた色とアイコンを自動設定
 * - 統一されたサイズと角丸
 *
 * @example
 * ```tsx
 * <RiskIconBox
 *   riskType="frost"
 *   riskLevel={2}
 *   size="md"
 *   hasRisk={true}
 * />
 * ```
 */
export const RiskIconBox: React.FC<RiskIconBoxProps> = ({
  riskType,
  riskLevel,
  grade,
  size = 'md',
  hasRisk = true,
  style,
}) => {
  const sizeConfig = BOX_SIZES[size];
  const config = RISK_DISPLAY_CONFIG[riskType];
  const iconName = getRiskIcon(riskType);

  let backgroundColor: string;
  if (grade !== undefined) {
    switch (grade) {
      case 'warning':
        backgroundColor = config?.highRiskColor ?? '#999999';
        break;
      case 'caution':
        backgroundColor = config?.lowRiskColor ?? '#99999930';
        break;
      default:
        backgroundColor = 'transparent';
    }
  } else {
    backgroundColor = getRiskColor(riskType, riskLevel ?? 0);
  }

  return (
    <Box
      style={{
        backgroundColor,
        borderRadius: `${layout.border.radius}px`,
        padding: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: sizeConfig.box,
        height: sizeConfig.box,
        ...style,
      }}
    >
      {hasRisk && <MaterialIcon icon={iconName} size={sizeConfig.icon} color="#fff" />}
    </Box>
  );
};
